import { createContext, useContext, useEffect, useState } from 'react';

const LANG_KEY = 'mynstur-lang';
export const DEFAULT_LANG = 'is';

// Copy sourced from the Mynstur brand manual (Vörumerkjarammi, 2. útgáfa, júlí 2026).
export const translations = {
  is: {
    nav: { cta: 'Hefja herferð', menu: 'Valmynd', close: 'Loka' },
    menu: {
      items: [
        { label: 'Kerfið', href: '#platform' },
        { label: 'Þjónusta', href: '#services' },
        { label: 'Árangur', href: '#results' },
        { label: 'Aðferð', href: '#approach' },
      ],
      getInTouch: 'Hafðu samband',
      follow: 'Fylgdu okkur',
      socials: [
        { label: 'LinkedIn', href: 'https://www.linkedin.com/company/93574038' },
      ],
    },
    hero: {
      title: { a: 'Rannsóknardrifin', em: 'birtingaþjónusta' },
      sub: 'Mynstur er óháð, rannsóknardrifin birtingaþjónusta sem teiknar fram mynstrin í hegðun markhópa og birtir markvisst á öllum miðlum. Öll gögn ávallt á borðinu.',
      ctaPrimary: 'Hefja herferð',
      ctaSecondary: 'Sjáðu mynstrið',
    },
    clients: { label: 'Treyst af íslenskum auglýsendum' },
    metrics: {
      intro: 'Tölur alltaf í samhengi — samanburður, viðmið, þróun.',
      items: [
        { value: '4,2 mia.+', label: 'Birtingar á mánuði', lead: true },
        { value: '38ms', label: 'Miðgildi svartíma' },
        { value: '+217%', label: 'Meðalaukning ROAS' },
        { value: '99,98%', label: 'Uppitími birtinga' },
      ],
    },
    services: {
      title: 'Þú veist alltaf hvað þú færð og hvers vegna.',
      lead: 'Engin tvö mynstur eru eins. Þess vegna byrjar hvert verkefni á rannsókn, ekki uppskrift.',
      link: 'Talaðu við teymið',
      items: [
        {
          title: 'Rannsóknardrifin',
          body: 'Rannsókn í hverju verkefni, ekki endurnýtt uppskrift. Skjalfest aðferðafræði; engin tvö mynstur eru eins.',
          tags: ['Rannsókn', 'Aðferðafræði'],
        },
        {
          title: 'AI ready',
          body: 'Gervigreind hraðar greiningunni og útfærslunni — manneskja ber ábyrgð. Mynsturgreining og hagræðing herferða í rauntíma.',
          tags: ['Gervigreind', 'Rauntími'],
        },
        {
          title: 'Hagkvæmni',
          body: 'Meira út úr hverri krónu. Óhæði og gögn lækka kostnað á snertingu.',
          tags: ['Kostnaður', 'Óhæði'],
        },
        {
          title: 'Innsýn',
          body: 'Skilningur, ekki bara skýrsla. Hver herferð skilar lærdómi um markhópinn sem nýtist áfram.',
          tags: ['Innsýn', 'Lærdómur'],
        },
      ],
    },
    platform: {
      title: { l1: 'Óháð.', l2: 'Gagnsætt. ', em: 'Ekkert í blindni.' },
      body: 'Mynstur á enga miðla, birtingasamninga né önnur tengsl sem hafa áhrif á val birtinga — gögnin þín ráða, ekki samningar okkar. Þar sem hefðbundnar stofur skila skýrslu í lok mánaðar skilar Mynstur skilningi í rauntíma: þú sérð hvað er birt, hvar, hvers vegna og hverju það skilar.',
      cta: 'Bóka kynningu',
    },
    approach: {
      title: 'Frá rannsókn til árangurs í þremur skrefum.',
      items: [
        {
          step: 'Rannsókn',
          title: 'Við teiknum fram mynstrið',
          body: 'Hvert verkefni byrjar á rannsókn, ekki uppskrift. Við drögum skýrar línur í hegðun markhópsins þíns og finnum mynstrið sem skiptir máli.',
        },
        {
          step: 'Birting',
          title: 'Gögnin ráða birtingunni',
          body: 'Rétt skilaboð, á réttum stað, á réttum tíma — á öllum miðlum. Valið stýrist af gögnum, ekki samningum eða eigin miðlum.',
        },
        {
          step: 'Innsýn',
          title: 'Þú sérð hverju það skilar',
          body: 'Skilningur í rauntíma, ekki skýrsla í lok mánaðar. Hver herferð skilar lærdómi um markhópinn sem nýtist í þeirri næstu.',
        },
      ],
    },
    ctaSection: {
      title: { pre: 'Sjáðu ', em: 'mynstrið', post: '.' },
      sub: 'Segðu okkur frá markmiðunum þínum. Við sýnum þér hvað þú færð og hvers vegna — ekkert í blindni.',
      placeholder: 'þu@fyrirtaeki.is',
      button: 'Fá tilboð',
      fine: 'Ekkert ruslpóst. Alvöru manneskja svarar innan dags.',
      sending: 'Sendi…',
      success: 'Takk! Við svörum þér innan dags.',
      error: 'Eitthvað fór úrskeiðis. Reyndu aftur eða sendu okkur línu á hallo@mynstur.is.',
    },
    footer: {
      intro: 'Við eigum enga miðla. Þess vegna ráða gögnin þín en ekki samningar okkar. Öll gögn ávallt á borðinu.',
      addr: ['Suðurlandsbraut 6, 7. hæð.', '108 Reykjavík'],
      email: 'hallo@mynstur.is',
      emailHref: 'mailto:hallo@mynstur.is',
      email2: 'bokhald@mynstur.is',
      email2Href: 'mailto:bokhald@mynstur.is',
      phone: '552 5100',
      phoneHref: 'tel:+3545525100',
    },
    consent: {
      text: 'Við notum vafrakökur til að mæla umferð. Greining keyrir aðeins ef þú samþykkir.',
      decline: 'Hafna',
      accept: 'Samþykkja',
    },
    switchTo: 'EN',
    switchLabel: 'Switch to English',
  },

  en: {
    nav: { cta: 'Start a campaign', menu: 'Menu', close: 'Close' },
    menu: {
      items: [
        { label: 'Platform', href: '#platform' },
        { label: 'Services', href: '#services' },
        { label: 'Results', href: '#results' },
        { label: 'Approach', href: '#approach' },
      ],
      getInTouch: 'Get in touch',
      follow: 'Follow',
      socials: [
        { label: 'LinkedIn', href: 'https://www.linkedin.com/company/93574038' },
      ],
    },
    hero: {
      title: { a: 'Research-driven', em: 'ad serving' },
      sub: 'Mynstur is an independent, research-driven media-buying service that draws out the patterns in audience behaviour and places ads with precision across every channel. All the data, always on the table.',
      ctaPrimary: 'Start a campaign',
      ctaSecondary: 'See the pattern',
    },
    clients: { label: 'Trusted by Icelandic advertisers' },
    metrics: {
      intro: 'Numbers always in context — comparison, benchmark, trend.',
      items: [
        { value: '4.2B+', label: 'Ads served monthly', lead: true },
        { value: '38ms', label: 'Median ad latency' },
        { value: '+217%', label: 'Avg. ROAS uplift' },
        { value: '99.98%', label: 'Delivery uptime' },
      ],
    },
    services: {
      title: 'You always know what you get, and why.',
      lead: 'No two patterns are alike. That is why every project starts with research, not a recipe.',
      link: 'Talk to our team',
      items: [
        {
          title: 'Research-driven',
          body: 'Research on every project, never a reused recipe. Documented methodology; no two patterns are alike.',
          tags: ['Research', 'Methodology'],
        },
        {
          title: 'AI ready',
          body: 'AI speeds up analysis and execution — a human stays accountable. Pattern analysis and real-time campaign optimisation.',
          tags: ['AI', 'Real-time'],
        },
        {
          title: 'Efficiency',
          body: 'More out of every króna. Independence and data lower the cost per contact.',
          tags: ['Cost', 'Independence'],
        },
        {
          title: 'Insight',
          body: 'Understanding, not just a report. Every campaign delivers learning about your audience that carries forward.',
          tags: ['Insight', 'Learning'],
        },
      ],
    },
    platform: {
      title: { l1: 'Independent.', l2: 'Transparent. ', em: 'Nothing blind.' },
      body: 'Mynstur owns no media, holds no placement deals and has no ties that influence where your ads run — your data decides, not our contracts. Where traditional agencies deliver a report at month-end, Mynstur delivers understanding in real time: you see what ran, where, why, and what it returned.',
      cta: 'Book a walkthrough',
    },
    approach: {
      title: 'From research to results in three moves.',
      items: [
        {
          step: 'Research',
          title: 'We draw out the pattern',
          body: 'Every project starts with research, not a recipe. We draw clear lines through your audience’s behaviour and find the pattern that matters.',
        },
        {
          step: 'Placement',
          title: 'The data decides the buy',
          body: 'The right message, in the right place, at the right moment — across every channel. Choices are driven by data, not contracts or owned media.',
        },
        {
          step: 'Insight',
          title: 'You see what it returns',
          body: 'Understanding in real time, not a report at month-end. Every campaign delivers learning about your audience that feeds the next one.',
        },
      ],
    },
    ctaSection: {
      title: { pre: 'See the ', em: 'pattern', post: '.' },
      sub: 'Tell us about your goals. We’ll show you what you get and why — nothing blind.',
      placeholder: 'you@company.is',
      button: 'Get a proposal',
      fine: 'No spam. A real human replies within the day.',
      sending: 'Sending…',
      success: 'Thanks! We’ll reply within the day.',
      error: 'Something went wrong. Try again or email hallo@mynstur.is.',
    },
    footer: {
      intro: 'We own no media. That is why your data decides, not our contracts. All the data, always on the table.',
      addr: ['Suðurlandsbraut 6, 7th floor', '108 Reykjavík'],
      email: 'hallo@mynstur.is',
      emailHref: 'mailto:hallo@mynstur.is',
      email2: 'bokhald@mynstur.is',
      email2Href: 'mailto:bokhald@mynstur.is',
      phone: '552 5100',
      phoneHref: 'tel:+3545525100',
    },
    consent: {
      text: 'We use cookies to measure traffic. Analytics only runs if you accept.',
      decline: 'Decline',
      accept: 'Accept',
    },
    switchTo: 'IS',
    switchLabel: 'Skipta yfir í íslensku',
  },
};

const LanguageContext = createContext({
  lang: DEFAULT_LANG,
  setLang: () => {},
  t: translations[DEFAULT_LANG],
});

export function LanguageProvider({ children }) {
  const [lang, setLang] = useState(() => {
    try {
      const saved = localStorage.getItem(LANG_KEY);
      if (saved === 'is' || saved === 'en') return saved;
    } catch {
      /* ignore */
    }
    return DEFAULT_LANG;
  });

  useEffect(() => {
    try {
      localStorage.setItem(LANG_KEY, lang);
    } catch {
      /* ignore */
    }
    document.documentElement.lang = lang;
  }, [lang]);

  const value = { lang, setLang, t: translations[lang] };
  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLang() {
  return useContext(LanguageContext);
}
